/**
 * Compact indicator sub-panels rendered under the main price chart.
 *
 * Each panel is a tiny SVG line/area chart aligned to the same time window
 * as the parent CandlestickChart so momentum divergences line up visually.
 *
 * Which panels render is driven by the `activeIndicators` list — same source
 * of truth as the chip toggles on AssetDetail.
 */
export default function IndicatorSubPanels({ candles, indicators, activeIndicators, height = 60 }) {
  if (!candles?.length || !indicators?.series) return null;
  const s = indicators.series;
  const panels = [
    { v: "rsi14", label: "RSI 14", series: s.rsi14, color: "#F59E0B",
      yMin: 0, yMax: 100, refs: [30, 70] },
    { v: "macd", label: "MACD", macd: s.macd, color: "#A855F7" },
    { v: "stoch", label: "Stochastic", k: s.stoch_k, d: s.stoch_d,
      color: "#3B82F6", yMin: 0, yMax: 100, refs: [20, 80] },
    { v: "adx14", label: "ADX 14", series: s.adx14, color: "#10B981",
      yMin: 0, yMax: 100, refs: [25] },
    { v: "atr14", label: "ATR 14", series: s.atr14, color: "#EC4899" },
    { v: "obv", label: "OBV", series: s.obv, color: "#94A3B8" },
  ].filter((p) => activeIndicators?.includes(p.v));

  if (!panels.length) return null;

  return (
    <div className="mt-2 space-y-1.5" data-testid="indicator-subpanels">
      {panels.map((p) => (
        <SubPanel key={p.v} panel={p} candles={candles} height={height} />
      ))}
    </div>
  );
}

function SubPanel({ panel, candles, height }) {
  const w = 900;          // logical width; SVG viewBox scales into parent width
  const padL = 4, padR = 40, padT = 12, padB = 4;
  const innerW = w - padL - padR;
  const innerH = height - padT - padB;

  // Values source: single-series OR MACD (line+signal+hist) OR Stoch (k+d)
  let raw = [];
  if (panel.macd) raw = panel.macd.line || [];
  else if (panel.k) raw = panel.k || [];
  else raw = panel.series || [];

  const finite = raw.filter((v) => v != null && !Number.isNaN(v));
  if (!finite.length) return null;

  const yMin = panel.yMin ?? Math.min(...finite);
  const yMax = panel.yMax ?? Math.max(...finite);
  const yRange = Math.max(yMax - yMin, 1e-9);
  const xStep = innerW / Math.max(candles.length - 1, 1);
  const yScale = (v) => padT + innerH - ((v - yMin) / yRange) * innerH;

  const pathOf = (arr) => {
    let d = "", first = true;
    for (let i = 0; i < arr.length; i++) {
      const v = arr[i];
      if (v == null) { first = true; continue; }
      const x = padL + i * xStep;
      const y = yScale(v);
      d += `${first ? "M" : "L"} ${x} ${y} `;
      first = false;
    }
    return d.trim();
  };

  const lastVal = panel.macd
    ? panel.macd.line?.slice(-1)[0]
    : panel.k
      ? panel.k?.slice(-1)[0]
      : panel.series?.slice(-1)[0];

  return (
    <div className="rounded-md border border-border bg-secondary/20 overflow-hidden" data-testid={`subpanel-${panel.v}`}>
      <svg viewBox={`0 0 ${w} ${height}`} width="100%" height={height} preserveAspectRatio="none">
        {/* Reference bands (e.g. 30/70 for RSI, 20/80 for Stoch, 25 for ADX) */}
        {(panel.refs || []).map((r, i) => {
          const y = yScale(r);
          return (
            <g key={`ref-${i}`}>
              <line x1={padL} y1={y} x2={padL + innerW} y2={y}
                    stroke="hsl(var(--muted-foreground))" strokeDasharray="2 3" strokeWidth="0.5" opacity="0.4" />
              <text x={padL + innerW + 3} y={y + 3} fill="hsl(var(--muted-foreground))"
                    fontSize="8" fontFamily="JetBrains Mono">{r}</text>
            </g>
          );
        })}

        {/* MACD: histogram bars + line + signal */}
        {panel.macd ? (
          <>
            {(panel.macd.hist || []).map((h, i) => {
              if (h == null) return null;
              const x = padL + i * xStep;
              const zeroY = yScale(0);
              const y = yScale(h);
              const bw = Math.max(1, xStep * 0.7);
              return (
                <rect key={`h-${i}`} x={x - bw / 2}
                      y={Math.min(y, zeroY)}
                      width={bw}
                      height={Math.abs(y - zeroY)}
                      fill={h >= 0 ? "hsl(var(--gain))" : "hsl(var(--loss))"}
                      opacity="0.45" />
              );
            })}
            <path d={pathOf(panel.macd.line || [])} stroke={panel.color} strokeWidth="1.2" fill="none" />
            <path d={pathOf(panel.macd.signal || [])} stroke="#F59E0B" strokeWidth="1" fill="none" strokeDasharray="3 2" />
          </>
        ) : panel.k ? (
          <>
            <path d={pathOf(panel.k)} stroke={panel.color} strokeWidth="1.2" fill="none" />
            <path d={pathOf(panel.d || [])} stroke="#F59E0B" strokeWidth="1" fill="none" strokeDasharray="3 2" />
          </>
        ) : (
          <path d={pathOf(panel.series || [])} stroke={panel.color} strokeWidth="1.2" fill="none" />
        )}

        {/* Panel label + latest value */}
        <text x={padL + 6} y={padT + 2} fill="hsl(var(--muted-foreground))"
              fontSize="9" fontFamily="JetBrains Mono">{panel.label}</text>
        {lastVal != null && (
          <text x={padL + innerW - 4} y={padT + 2} textAnchor="end"
                fill={panel.color} fontSize="9" fontFamily="JetBrains Mono">
            {Number(lastVal).toFixed(2)}
          </text>
        )}
      </svg>
    </div>
  );
}
